import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import React, { useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import ModeSwitch, { ModeOption } from '../../components/ModeSwitch'
import DateInput from '../../components/inputs/DateInput'
import { ObjectContext } from '../../contexts/objectContext/ObjectContext'
import { trpc } from '../../modules/trpc'
import PriceInput from '../../pages/contractInformation/components/PriceInput'
import { ContractSlideStateType } from '../types/types'

interface Props {
	onInteraction: () => void
	setOnInteractionFunc?: React.Dispatch<React.SetStateAction<(() => void) | undefined>>
	state: [ContractSlideStateType, React.Dispatch<React.SetStateAction<ContractSlideStateType>>]
	onboarding: boolean
}

const ContractTypeSlide: React.FC<Props> = ({
	onInteraction,
	setOnInteractionFunc,
	state,
	onboarding,
}) => {
	const object = useContext(ObjectContext)
	if (!object) return null

	const queryClient = useQueryClient()
	const { t } = useTranslation('pages_onboarding')

	const { mutate: updateObject } = trpc.objects.edit.useMutation({
		onSuccess: () => {
			// After updating object details invalidate all queries to force just-in-time refresh when data is accessed the next time.
			// Invalidating all queries ensures everything that is dependant on price is refetched and calculated.
			queryClient.invalidateQueries()
			onInteraction()
		},
	})

	const submit = () => {
		if (state[0].contractType === null || state[0].contractType === undefined) return
		updateObject({
			id: object.id,
			data: {
				monthlyPrice: parseFloat(state[0].monthlyPrice ?? '0'),
				spotMarginal: parseFloat(state[0].spotMarginal ?? '0'),
				contractType: state[0].contractType,
				fixedPrice: parseFloat(state[0].fixedPrice ?? '0'),
			},
		})
	}

	useEffect(() => {
		if (setOnInteractionFunc) setOnInteractionFunc(() => submit)
	}, [state[0]])

	const modeOptions: ModeOption<'spot' | 'fixed'>[] = [
		{ mode: 'spot', label: 'Pörssisähkö' },
		{ mode: 'fixed', label: 'Kiinteä sähkö' },
	]

	return (
		<div className='text-text-dark dark:text-white'>
			{onboarding && <h1>{t('contractTypeSlide.millainenSopimus')}</h1>}

			<ModeSwitch
				options={modeOptions}
				currentMode={state[0].contractType ?? 'fixed'}
				onModeChange={newMode => state[1](prevState => ({ ...prevState, contractType: newMode }))}
			/>

			{state[0].contractType === 'spot' ? (
				<div className='input-spacer'>
					<PriceInput
						title={t('contractTypeSlide.marginaali')}
						unit='snt/kWh'
						onValueChange={value => state[1]({ ...state[0], spotMarginal: value })}
						value={state[0].spotMarginal}
					/>
					<PriceInput
						title={t('contractTypeSlide.perusmaksu')}
						unit='eur/kk'
						onValueChange={value => state[1]({ ...state[0], monthlyPrice: value })}
						value={state[0].monthlyPrice}
					/>
				</div>
			) : (
				<div className='input-spacer'>
					<PriceInput
						title={t('contractTypeSlide.hinta')}
						unit='snt/kWh'
						onValueChange={value => state[1]({ ...state[0], fixedPrice: value })}
						value={state[0].fixedPrice}
					/>
					<PriceInput
						title={t('contractTypeSlide.perusmaksu')}
						unit='eur/kk'
						onValueChange={value => state[1]({ ...state[0], monthlyPrice: value })}
						value={state[0].monthlyPrice}
					/>
					<DateInput
						minDate={dayjs().format('YYYY-MM-DD')}
						label={t('contractTypeSlide.endDate')}
						value={state[0].contractEndDate ?? ' '}
						onChange={(value: string) => {
							state[1]({ ...state[0], contractEndDate: dayjs(value).format('YYYY-MM-DD') })
						}}
					/>
				</div>
			)}
		</div>
	)
}

export default ContractTypeSlide
